

import * as Yup from 'yup';
import REGEX from '../../../Constants/Regex';

const MB = 1024 * 1024;

const PROFILE_FILE_SIZE = 2;
const BANNER_FILE_SIZE = 5;
const AADHAR_CARD_FILE_SIZE = 3;
const TRAINER_CERTIFICATES_FILE_SIZE = 10;
const LEASE_AGREEMENT_FILE_SIZE = 1;

const FILE_SIZES = {
    PROFILE_FILE_SIZE,
    BANNER_FILE_SIZE,
    REGISTRATION_DOCS_FILE_SIZE: AADHAR_CARD_FILE_SIZE,
    AADHAR_CARD_FILE_SIZE,
    PAN_CARD_FILE_SIZE: AADHAR_CARD_FILE_SIZE,
    TRAINER_CERTIFICATES_FILE_SIZE,
    LEASE_AGREEMENT_FILE_SIZE,
    ELECTRICITY_BILL_FILE_SIZE: LEASE_AGREEMENT_FILE_SIZE,
};

const _getFileSize = (size = 0) => {
    return (size * MB)
}

const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const PDF_TYPES = ["application/pdf"];

/** Helper — check file type */
const isFileType = (file, types) => !!file && types.includes(file.type);

const socialLinksSchema = Yup.object().shape({
    instagram: Yup.string().url('Must be a valid URL').nullable().optional(),
    facebook: Yup.string().url('Must be a valid URL').nullable().optional(),
    linkedin: Yup.string().url('Must be a valid URL').nullable().optional(),
    youtube: Yup.string().url('Must be a valid URL').nullable().optional(),
});

const fullHoursSchema = Yup.object().shape({
    Mon: Yup.string(),
    Tue: Yup.string(),
    Wed: Yup.string(),
    Thu: Yup.string(),
    Fri: Yup.string(),
    Sat: Yup.string(),
    Sun: Yup.string(),
});

const youtubeLinkItemSchema = Yup.object().shape({
    link: Yup.string()
        .trim()
        .test(
            "is-youtube-url",
            "Invalid YouTube URL",
            (value) => {
                if (!value) return true; // allow empty
                return REGEX.YOUTUBE_REGEX.test(value);
            }
        ),

    title: Yup.string().when("link", {
        is: (val) => !!val,
        then: (schema) => schema.required("Title is required when URL is provided"),
        otherwise: (schema) => schema.optional(),
    }),

    channel: Yup.string().optional(),
    views: Yup.string().optional(),
});


/** Helper — check file with document fallback */
const fileValidation = (label, allowedTypes, sizeLimit, isPdf = false) => {
    // helper to get id from URL (either path like /my-profile/123 or query ?id=123)
    const getIdFromUrl = () => {
        if (typeof window === 'undefined') return null;
        const segments = window.location.pathname.split("/");
        return !isNaN(segments?.[3]) ? segments[3] : null;
    };

    return Yup.mixed()
        .test(`${label}-required`, `${label} is required`, function (file) {
            const { documents } = this.parent;

            // 1) if backend provided document → not required
            if (!file && !!documents && documents[label]) return true;

            // 2) if user is on /my-profile/:id (or ?id=) → not required
            const idFromUrl = getIdFromUrl();
            const isOnMyProfile = typeof window !== 'undefined' && window.location.pathname.includes('/my-profile');

            if (isOnMyProfile || idFromUrl) return true;

            // 3) otherwise file is required
            return !!file;
        })
        .test(`${label}-type`, isPdf ? "Only PDF files are allowed" : "Only images are allowed (jpg, png, jpeg, webp)", function (file) {
            if (!file) return true; // nothing to validate (handled by required test)
            return isFileType(file, allowedTypes);
        })
        .test(`${label}-size`, `${label} must be ≤ ${sizeLimit} MB`, function (file) {
            if (!file) return true;
            return file.size <= _getFileSize(sizeLimit);
        });
};

// Validation Schema
const validationSchema = Yup.object({
    facility_name: Yup.string()
        .required('Facility name is required')
        .min(2, 'Facility name must be at least 2 characters')
        .max(100, 'Facility name must not exceed 100 characters'),

    user_name: Yup.string()
        .transform(value => {
            if (typeof value !== "string") return value;
            // force lowercase and remove invalid chars
            return value.toLowerCase().replace(/[^a-z1-9_]/g, "");
        })
        .required("Username is required")
        .min(3, "Type at least 3 characters")
        .matches(/^[a-z1-9_]+$/, "Only lowercase letters (a–z), digits (1–9) and underscore (_) allowed"),

    owner_first_name: Yup.string().required('Owner first name is required').min(2, 'Minimum 2 characters'),
    
    owner_last_name: Yup.string().required('Owner last name is required').min(2, 'Minimum 2 characters'),

    email: Yup.string()
        .required('Email is required')
        .email('Must be a valid email address'),

    est_year: Yup.string()
        .required('Establishment year is required')
        .matches(/^\d{4}$/, 'Must be a valid year')
        .test('valid-year', 'Year must be between 1950 and current year', function (value) {
            if (!value) return false;
            const year = parseInt(value);
            const currentYear = new Date().getFullYear();
            return year >= 1900 && year <= currentYear;
        }),

    phone_no: Yup.string()
        .required('Contact number is required')
        .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits'),

    whatsapp_no: Yup.string()
        .matches(/^[0-9]{10}$/, 'WhatsApp number must be exactly 10 digits'),

    academic_qualification: Yup.string()
        .required('Academic qualification is required'),

    city_id: Yup.string()
        .required('City is required'),

    state_id: Yup.string()
        .required('State is required'),

    country_id: Yup.string()
        .required('Country is required'),

    pincode: Yup.string()
        .required('Pincode is required')
        .matches(/^[0-9]{6}$/, 'Pincode must be exactly 6 digits'),

    full_address: Yup.string()
        .required('Full address is required')
        .min(10, 'Address must be at least 10 characters')
        .max(500, 'Address must not exceed 500 characters'),

    navigation_url: Yup.string()
        .url('Must be a valid URL'),

    experiance_yrs: Yup.string()
        .required('Experience years is required')
        .matches(/^\d+$/, 'Must be a valid number'),

    social_links: socialLinksSchema,

    notes: Yup.string()
        .max(1000, 'Notes must not exceed 1000 characters'),

    key_points: Yup.mixed(),

    pricing_plans: Yup.array()
        .min(1, 'At least one pricing plan is required')
        .of(Yup.mixed()),

    amenities: Yup.array().of(Yup.object()),

    full_hours: fullHoursSchema,

    services: Yup.array().min(1, 'Please add at least 1 Service').required('Please add Services'),

    youtube_links: Yup.array()
        .of(youtubeLinkItemSchema)
        .optional(),

    codeOfEthics: Yup.boolean().oneOf([true], 'You must agree to the Code of Ethics').required(),

    logo_img: fileValidation("logo_img", IMAGE_TYPES, PROFILE_FILE_SIZE),

    banner_img: fileValidation("banner_img", IMAGE_TYPES, BANNER_FILE_SIZE),

    aadhar_card: fileValidation("aadhar_card", PDF_TYPES, AADHAR_CARD_FILE_SIZE),

    pan_card: fileValidation("pan_card", PDF_TYPES, AADHAR_CARD_FILE_SIZE),

    registration_docs: fileValidation("registration_docs", PDF_TYPES, AADHAR_CARD_FILE_SIZE),

    trainer_certificates: fileValidation("trainer_certificates", PDF_TYPES, TRAINER_CERTIFICATES_FILE_SIZE),

    electricity_bill: fileValidation("electricity_bill", PDF_TYPES, LEASE_AGREEMENT_FILE_SIZE),

    lease_agreement: fileValidation("lease_agreement", PDF_TYPES, LEASE_AGREEMENT_FILE_SIZE)

});

export { validationSchema, FILE_SIZES }